import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import ReservationTimeSelector from "./ReservationTimeSelector";
import { useAuth } from "../util/auth";

const ReservationForm = ({
  onSubmit,
  initialValues = {},
  submitButtonText = "Submit",
  submitButtonDisabled = false,
  excludeReservationId = null,
  onCancel,
  showCancelButton = false,
  cancelButtonText = "Cancel",
  className = "",
  disableSeatTypeSelection = false,
  isAdmin = false
}) => {
  const auth = useAuth();
  const [selectedDate, setSelectedDate] = useState(initialValues.date || null);
  const [selectedTime, setSelectedTime] = useState(initialValues.time || null);
  const [selectedSeats, setSelectedSeats] = useState(initialValues.tableIds || []);
  const [seatType, setSeatType] = useState(initialValues.seatType || 'table');
  const [isSelectionConfirmed, setIsSelectionConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleSubmit, register, errors, watch, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      date: initialValues.date || "",
      partySize: initialValues.partySize || "",
      name: initialValues.name || "",
      phoneNumber: initialValues.phoneNumber || "",
      seatType: initialValues.seatType || "table",
      reminderEmail: initialValues.reminderEmail || auth.user?.email || ""
    },
  });

  // Set confirmation state based on initial values (for editing existing reservations)
  useEffect(() => {
    if (initialValues.time && initialValues.tableIds && initialValues.tableIds.length > 0) {
      setIsSelectionConfirmed(true);
    }
  }, [initialValues.time, initialValues.tableIds]);

  const partySize = watch("partySize");

  // Helper function to check if a date is a Monday
  const isMonday = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getDay() === 1;
  };

  // Helper function to check if a date is blocked
  const isBlockedDate = (dateStr) => {
    if (dateStr === '2025-11-27') return true;
    
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Block June 28 - July 7, 2026 (summer closure)
    if (year === 2026 && month === 6 && day >= 28) return true;
    if (year === 2026 && month === 7 && day <= 7) return true;
    
    // Block December 25 (Christmas) and December 28, 2025 - January 5, 2026 (winter closure)
    if (year === 2025 && month === 12 && (day === 25 || day >= 28)) return true;
    if (year === 2026 && month === 1 && day <= 5) return true;
    
    // Block November 26, 2026 (Thanksgiving)
    if (dateStr === '2026-11-26') return true;
    
    // Block December 25, 2026 - January 4, 2027 (winter closure, reopening January 5)
    if (year === 2026 && month === 12 && day >= 25) return true;
    if (year === 2027 && month === 1 && day <= 4) return true;
    
    return false;
  };

  // Helper function to get the next available date
  const getNextAvailableDate = () => {
    // Create date in LA timezone
    const laDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    let nextDate = new Date(laDate);
    
    // If not admin, start from tomorrow
    if (!isAdmin) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    
    // Skip Mondays (restaurant closed)
    while (isMonday(nextDate.toISOString().split('T')[0])) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    return nextDate.toISOString().split('T')[0];
  };

  // Helper function to get the maximum allowed date
  const getMaxAllowedDate = () => {
    // Create date in LA timezone
    const laDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const maxDate = new Date(laDate);
    
    if (isAdmin) {
      // Admin users can book 2 years in advance
      maxDate.setFullYear(laDate.getFullYear() + 2);
    } else {
      // General users can book 30 days in advance
      maxDate.setDate(laDate.getDate() + 30);
    }
    
    return maxDate.toISOString().split('T')[0];
  };

  const handleFormSubmit = async (data) => {
    // Prevent multiple concurrent submissions
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Add comprehensive debugging information
      console.log('Form submission data:', {
        formData: data,
        selectedTime,
        selectedSeats,
        initialTime: initialValues.time,
        partySize,
        seatType
      });
      
      // Ensure we have a time selected before submitting
      if (!selectedTime) {
        console.error('No time selected at form submission');
        
        // Check if data.time exists - this happens when the form is coming from initialValues
        // but the state hasn't been updated
        if (initialValues.time && typeof initialValues.time === 'string') {
          console.log('Using initialTime as fallback:', initialValues.time);
          
          // Use initialTime as a fallback
          await onSubmit({
            ...data,
            // Remove dashes from phone number before submitting
            phoneNumber: data.phoneNumber.replace(/\D/g, ''),
            time: initialValues.time,
            seatType: seatType,
            tableIds: selectedSeats.length > 0 ? selectedSeats : initialValues.tableIds || []
          });
          return;
        }
        
        alert('Please select a time for your reservation');
        return;
      }
      
      await onSubmit({
        ...data,
        // Remove dashes from phone number before submitting
        phoneNumber: data.phoneNumber.replace(/\D/g, ''),
        time: selectedTime,
        seatType: seatType,
        tableIds: selectedSeats
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format phone number with dashes
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Apply formatting based on length
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  // Helper function to format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to format time for display
  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const isFormDisabled = submitButtonDisabled || isSubmitting || !selectedTime || !isSelectionConfirmed;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={`space-y-4 ${className}`}>
      <div>
        <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="name">
          Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base"
          ref={register({
            required: "Please enter your name",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters long"
            }
          })}
        />
        {errors.name && (
          <p className="text-sm text-left text-red-600 mt-1">
            {errors.name.message}
          </p>
        )}
      </div>

      <div>
        <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="phoneNumber">
          Phone Number
        </label>
        <input
          type="tel"
          id="phoneNumber"
          name="phoneNumber"
          className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base"
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value);
            e.target.value = formatted;
            setValue("phoneNumber", formatted);
          }}
          ref={register({
            required: "Please enter your phone number",
            pattern: {
              value: /^\d{3}-\d{3}-\d{4}$/,
              message: "Please enter a valid phone number"
            }
          })}
        />
        {errors.phoneNumber && (
          <p className="text-sm text-left text-red-600 mt-1">
            {errors.phoneNumber.message}
          </p>
        )}
      </div>

      <div>
        <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="date">
          Date
        </label>
        <input
          type="date"
          id="date"
          name="date"
          className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base"
          min={getNextAvailableDate()}
          max={getMaxAllowedDate()}
          onChange={(e) => {
            const date = e.target.value;
            if (!isMonday(date) && !isBlockedDate(date)) {
              setSelectedDate(date);
              setSelectedTime(null);
              setSelectedSeats([]); // Clear selected seats when date changes
              setIsSelectionConfirmed(false);
            }
          }}
          ref={register({
            required: "Please select a date",
            validate: {
              notMonday: value => !isMonday(value) || "We are closed on Mondays",
              notBlocked: value => !isBlockedDate(value) || "This date is not available for reservations"
            }
          })}
          lang="en-US"
        />
        {errors.date && (
          <p className="text-sm text-left text-red-600 mt-1">
            {errors.date.message}
          </p>
        )}
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          Hours: Dinner (Tue-Sat 17:00-21:00)
        </p>
      </div>

      <div>
        <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="partySize">
          Number of Guests
        </label>
        <select
          id="partySize"
          name="partySize"
          className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base"
          onChange={(e) => {
            setValue("partySize", e.target.value);
            setSelectedTime(null);
            setSelectedSeats([]); // Clear selected seats when party size changes
            setIsSelectionConfirmed(false);
          }}
          ref={register({
            required: "Please select the number of guests",
            validate: value => !isNaN(parseInt(value)) || "Please select a valid number of guests"
          })}
        >
          <option value="">Select number of guests</option>
          {[1, 2, 3, 4, 5, 6].map((num) => (
            <option key={num} value={num}>
              {num} {num === 1 ? 'person' : 'people'}
            </option>
          ))}
        </select>
        {errors.partySize && (
          <p className="text-sm text-left text-red-600 mt-1">
            {errors.partySize.message}
          </p>
        )}
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          For reservations for parties of 7 or more, please call us at <a href="tel:650-344-0888" className="text-blue-600 hover:text-blue-800">650-344-0888</a>
        </p>
      </div>

      {!disableSeatTypeSelection ? (
        <div>
          <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="seatType">
            Seat Type
          </label>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => {
                setSeatType('table');
                setSelectedSeats([]); // Clear selected seats when seat type changes
                setIsSelectionConfirmed(false);
              }}
              className={`px-4 py-2 rounded border ${
                seatType === 'table'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-blue-500'
              }`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => {
                setSeatType('counter');
                setSelectedSeats([]); // Clear selected seats when seat type changes
                setIsSelectionConfirmed(false);
              }}
              className={`px-4 py-2 rounded border ${
                seatType === 'counter'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-blue-500'
              }`}
            >
              Counter
            </button>
          </div>
        </div>
      ) : (
        <div>
          <label className="block mb-1 font-medium text-sm sm:text-base">
            Seat Type
          </label>
          <div className="px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700">
            {seatType === 'counter' ? 'Counter' : 'Table'} (cannot be changed)
          </div>
        </div>
      )}

      {selectedDate && partySize && (
        <div className="mt-4">
          <label className="block mb-2 font-medium text-sm sm:text-base">
            Available Times
          </label>
          <ReservationTimeSelector
            key={`${selectedDate}-${partySize}-${seatType}-${isAdmin}`}
            selectedDate={selectedDate}
            partySize={parseInt(partySize)}
            onTimeSelect={(time, seats) => {
              setSelectedTime(time);
              setSelectedSeats(seats);
            }}
            selectedTime={selectedTime}
            excludeReservationId={excludeReservationId}
            disabled={submitButtonDisabled}
            seatType={seatType}
            isAdmin={isAdmin}
            onConfirmationChange={setIsSelectionConfirmed}
          />
        </div>
      )}

      <div>
        <label className="block mb-1 font-medium text-sm sm:text-base" htmlFor="reminderEmail">
          Reminder Email
        </label>
        <input
          type="email"
          id="reminderEmail"
          name="reminderEmail"
          className="block border placeholder-gray-400 w-full rounded border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 px-3 sm:px-4 py-2 sm:py-3 leading-6 text-sm sm:text-base"
          ref={register({
            required: "Please enter an email address for reminders",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Please enter a valid email address"
            }
          })}
        />
        {errors.reminderEmail && (
          <p className="text-sm text-left text-red-600 mt-1">
            {errors.reminderEmail.message}
          </p>
        )}
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          We'll send your reservation reminder to this email address
        </p>
      </div>

      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
        <button
          type="submit"
          disabled={isFormDisabled}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Processing...' : submitButtonText}
        </button>
        {showCancelButton && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {cancelButtonText}
          </button>
        )}
      </div>
    </form>
  );
};

export default ReservationForm; 